import {
  DashboardState,
  EProjectType,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IRecentlyOpenedScreensDto,
  IScreenDesignInfo,
  IScreenFolder,
  LeftPanelType,
  RightPanelType,
  ViewPart
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { DragDropContext, Draggable, DropResult, Droppable } from "react-beautiful-dnd";
import { connect } from "react-redux";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import {
  addDesignerItemToFolder,
  addDesignerItemToSubfolder,
  deleteDesignerFolder,
  deleteDesignerSubfolder,
  removeDesignerItemFromFolder,
  removeDesignerItemFromSubfolder
} from "../../../../shared/utility/designer-folder-actions";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { getScreenRoleIcon as getScreenRoleIconShared } from "../../../../shared/utility/screen-role-icon";
import {
  buildScreenActionMenu,
  canOpenDeleteScreenModal,
  deleteScreenFromRow,
  duplicateScreenFromRow,
  exportScreenKuikaFile,
  getScreenTypeOf,
  hasAnyScreenRowAction,
  renameCurrentScreen,
  type DesignType,
  type ScreenRenameOutcome
} from "../../../../shared/utility/screen-row-actions";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaDropdownMenu, {
  type KuikaDropdownMenuItemType
} from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { redirectToProcessForms } from "../../flows/process-editor/redirect-to-process-forms";
import { ReportCreateModal } from "../../report-designer/report-create-modal";
import SearchablePanelHeader from "../searchable-panel-header/searchable-panel-header";
import RecentlyVisitedRegion from "./recently-visited-region";
import { readRememberedRowCount, rememberRowCount } from "./recently-visited-row-memory";
import { ScreenAddComponent } from "./screen-design-add";
import { ScreenFolderAddComponent } from "./screen-folder-add";
import "./screens.scss";

export interface IPendingScreenFolderTarget {
  folderId: string;
  parentFolderId?: string;
}

const DEFAULT_ACTIVE_SECTION_KEYS: string[] = [
  "screens",
  "master",
  "recentlyOpened",
  "workflow-screens-panel",
  "reports"
];

export interface IScreensWindowState {
  isSearchActive: boolean;
  searchText: string;
  editingFolder: IScreenFolder | null;
  screenToDelete: IScreenDesignInfo | null;
  activeKeys: string[];
  activeSectionKeys: string[];
  recentlyOpenedScreens: IRecentlyOpenedScreensDto[];
  /** The recently-visited rows have a request of their own, so they arrive after the rest of the panel. */
  isRecentlyOpenedLoading: boolean;
  /** The row count this project settled at last time; `undefined` until one has been remembered. */
  expectedRecentRows: number | undefined;
  creatingScreenType: EScreenType;
  folderParentFolderId: string | null | undefined;
  pendingScreenFolderTarget: IPendingScreenFolderTarget | null;
  modals: {
    newScreen: boolean;
    newFolder: boolean;
    editFolder: boolean;
    deleteScreen: boolean;
    /** The "create a blank report" sheet — the Reports panel's own "+" and its empty state. */
    newReport: boolean;
  };
  renamingScreenId: string;
  isScreenRenaming: boolean;
  isRenameLoading: boolean;
  /** A screen or folder row is being dragged: the selection indicator steps aside until the drop. */
  isRowDragging: boolean;
}

export type { DesignType };

export interface IScreensWindowProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  panelName: string;
  designType: DesignType;
}

class ScreensWindow extends PureSdComponent<
  IScreensWindowProps & IWithSDProps & IWithContextProp & IWithHistoryProp,
  IScreensWindowState
> {
  constructor(props: IScreensWindowProps & IWithSDProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      isSearchActive: false,
      searchText: "",
      editingFolder: null,
      screenToDelete: null,
      activeKeys: [],
      activeSectionKeys: [...DEFAULT_ACTIVE_SECTION_KEYS],
      recentlyOpenedScreens: [],
      // componentDidMount asks for them immediately, so the section is loading from its first frame.
      isRecentlyOpenedLoading: true,
      expectedRecentRows: undefined,
      creatingScreenType: EScreenType.Default,
      folderParentFolderId: "",
      pendingScreenFolderTarget: null,
      modals: {
        newScreen: false,
        newFolder: false,
        editFolder: false,
        deleteScreen: false,
        newReport: false
      },
      renamingScreenId: "",
      isScreenRenaming: false,
      isRenameLoading: false,
      isRowDragging: false
    };
  }

  async componentDidMount() {
    this.reloadScreenList();
    this.handleDefaultOpenFolder();
    this.handleSearchTextOnInit();
    this.getRecentlyOpenedScreens();
  }

  componentWillUnmount() {
    this.clearExpandHover();
  }

  componentDidUpdate = async (prevProps, prevState) => {
    if (this.props.sd.isScreenWindowReload) {
      await this.reloadScreenList();
      this.props.sd.setIsScreenWindowReload(false);
      this.handleDefaultOpenFolder();
    }
    if (prevState.modals.newFolder === true && this.state.modals.newScreen === false) {
      await this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Datasources);
      await this.props.sd.setActiveLeftPanelByClick(LeftPanelType.Screens);
    }
  };

  handleSearchTextOnInit = () => {
    let searchText;
    if (this.props.designType === "report") {
      searchText = this.props.sd.searchTexts[ViewPart.ReportDesignerPagesPanel];
    } else {
      searchText = this.props.sd.searchTexts[ViewPart.ScreensPanel];
    }
    if (searchText && searchText.length > 0) {
      this.setState({ isSearchActive: true, searchText });
    }
  };

  openModal = (modalKey: keyof IScreensWindowState["modals"], additionalState = {}) => {
    this.setState((prevState: IScreensWindowState) => ({
      modals: { ...prevState.modals, [modalKey]: true },
      ...additionalState
    }));
  };

  closeModal = (modalKey: keyof IScreensWindowState["modals"], additionalState = {}) => {
    this.setState((prevState) => ({
      modals: { ...prevState.modals, [modalKey]: false },
      ...additionalState
    }));
  };

  /**
   * The folder a pending create should land in. Normally captured in local state when the
   * panel's own folder "+" opens the modal; when the modal is opened from outside the panel
   * (the row-2 screen selector's per-folder "+") the target arrives on the shared context
   * instead, since that flag is the only channel those callers have.
   */
  private findParentFolderId = (folders: IScreenFolder[], folderId: string): string | undefined => {
    for (const folder of folders) {
      const children: IScreenFolder[] = folder.children ?? [];
      if (children.some((child: IScreenFolder) => child.id === folderId)) return folder.id;
      const deeper: string | undefined = this.findParentFolderId(children, folderId);
      if (deeper) return deeper;
    }
    return undefined;
  };

  private getPendingFolderTarget = (): IPendingScreenFolderTarget | null => {
    if (this.state.pendingScreenFolderTarget) return this.state.pendingScreenFolderTarget;
    const sharedFolderId: string | null = this.props.sd.pendingScreenFolderTargetId;
    if (!sharedFolderId) return null;
    // assignNewScreenToFolder needs the parent id to pick the subfolder write path.
    return {
      folderId: sharedFolderId,
      parentFolderId: this.findParentFolderId(this.props.sd.project?.screenFolders ?? [], sharedFolderId)
    };
  };

  /**
   * Type the new-screen modal should create. The panel picks it from its own local state; when the
   * modal is opened from outside (the shared visibility flag) the caller has no way into that
   * state, so it may set `pendingNewScreenType` instead — which is how the row-2 selector asks for
   * a Master. Without such a request an outside open still means a plain screen.
   */
  private getCreatingScreenType = (): EScreenType => {
    if (!this.props.sd.isNewScreenModalVisible) {
      return this.state.creatingScreenType;
    }
    const requested: EScreenType | null = this.props.sd.pendingNewScreenType;
    if (requested !== null) {
      return requested;
    }
    return this.state.creatingScreenType === EScreenType.WebScreen ? EScreenType.WebScreen : EScreenType.Default;
  };

  closeNewScreenModal = async (newScreenId?: any) => {
    const folderTarget: IPendingScreenFolderTarget | null = this.getPendingFolderTarget();
    this.closeModal("newScreen", { pendingScreenFolderTarget: null });
    this.props.sd.setPendingScreenFolderTargetId(null);
    this.props.sd.setPendingNewScreenType(null);
    this.props.sd.setNewScreenModalVisibility(false);
    if (newScreenId) {
      this.assignNewScreenToFolder(folderTarget, newScreenId);
      await this.reloadScreenList();
      this.props.sd.loadScreen(newScreenId, false, true);
    }
  };

  // A screen created from inside a folder must land in that folder, mirroring the drag-into-folder
  // path (onDragEnd → addDesignerItemToFolder). The create-screen service has no folder concept, so
  // the membership is written here, after creation, from the folder context captured when the modal
  // opened. The screen id may be undefined for CRUD/Excel multi-screen flows — in that case there is
  // no single screen to place, so we skip.
  assignNewScreenToFolder = (folderTarget: IPendingScreenFolderTarget | null, newScreenId: unknown): void => {
    if (!folderTarget || typeof newScreenId !== "string" || newScreenId.length === 0) {
      return;
    }
    if (folderTarget.parentFolderId) {
      addDesignerItemToSubfolder(getSdInstance(), {
        folderType: "screen",
        parentFolderId: folderTarget.parentFolderId,
        subFolderId: folderTarget.folderId,
        itemId: newScreenId
      });
      this.ensureFoldersExpanded([folderTarget.parentFolderId, folderTarget.folderId]);
    } else {
      addDesignerItemToFolder(getSdInstance(), {
        folderType: "screen",
        folderId: folderTarget.folderId,
        itemId: newScreenId
      });
      this.ensureFoldersExpanded([folderTarget.folderId]);
    }
  };

  async reloadScreenList() {
    try {
      this.props.sd.setScreenList();
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  }

  /**
   * The rows the section ends up with once a search is cleared — what its height is remembered as.
   * The search filter is deliberately left out: it narrows what is drawn now, not what this project
   * settles at.
   */
  countRecentlyOpenedRows = (rows: IRecentlyOpenedScreensDto[]): number => {
    return rows.filter((row) => {
      const screenInfo = this.props.sd.screenList?.find((s) => s.id === row.screenId);
      const isReport: boolean = screenInfo ? this.getScreenType(screenInfo) === EScreenType.Report : false;
      return !isReport && row.screenId !== this.props.sd.sdId;
    }).length;
  };

  getRecentlyOpenedScreens = async () => {
    this.setState({
      isRecentlyOpenedLoading: true,
      expectedRecentRows: readRememberedRowCount(this.props.sd.project?.id)
    });
    try {
      const res: any = await ScreenService.GetRecentlyOpenedScreens(
        this.props.context.userInfo.id,
        6,
        this.props.sd.project?.id,
        // Reports are recorded in the same feed but belong to the Report Designer, not here. Ask
        // the server to leave them out so the six rows are six screens — the render-time filter
        // below would otherwise thin the list down whenever reports crowd the top.
        false
      );
      if (res.status === 200) {
        const rows: IRecentlyOpenedScreensDto[] = res.data;
        // The height the next visit may draw a placeholder at.
        rememberRowCount(this.props.sd.project?.id, this.countRecentlyOpenedRows(rows));
        this.setState({ recentlyOpenedScreens: rows });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      // Also on failure: a section that keeps shimmering after a failed request reads as a hang.
      this.setState({ isRecentlyOpenedLoading: false });
    }
  };

  handleDefaultOpenFolder = () => {
    const { activeKeys } = this.state;
    const currScreenId = KMainFunctions.getSdIdFromUrl();
    const defaultOpenFolder = this.props?.sd?.project?.screenFolders?.find((f) =>
      f.screenIds?.find((x) => x === currScreenId)
    );
    if (defaultOpenFolder) {
      this.setState({ activeKeys: [...activeKeys, defaultOpenFolder.id] });
    }
  };

  isScreenCurScreen = (sdId): boolean => {
    return KMainFunctions.getSdIdFromUrl() === sdId;
  };

  getScreenRoleIcon = (sdId: string, isManaged?: boolean): JSX.Element => {
    return getScreenRoleIconShared(this.props.sd, sdId, isManaged);
  };

  openNewScreenModal = (
    screenListCount: number,
    screenType: EScreenType,
    folderTarget?: IPendingScreenFolderTarget
  ) => {
    const accessName = "Screens";
    if (KMainFunctions.isDomainHasAccess(accessName, screenListCount) === false) {
      this.props.showDomainAccessRequiredModal(accessName);
      return;
    }
    this.openModal("newScreen", {
      creatingScreenType: screenType,
      pendingScreenFolderTarget: folderTarget ?? null
    });
  };

  onFolderPanelChange = (key) => {
    this.setState({ activeKeys: key });
  };

  // Section keys are kept apart from `activeKeys`; sharing them closed open folders (KOA-14403).
  onSectionPanelChange = (keys: React.Key[]) => {
    this.setState({ activeSectionKeys: keys.map((key: React.Key): string => String(key)) });
  };

  ensureFoldersExpanded = (folderIds: (string | undefined)[]) => {
    const idsToOpen: string[] = folderIds.filter((id): id is string => Boolean(id));
    if (idsToOpen.length === 0) {
      return;
    }
    this.setState((prevState: IScreensWindowState) => {
      const next: Set<string> = new Set(prevState.activeKeys);
      idsToOpen.forEach((id: string) => next.add(id));
      return { activeKeys: Array.from(next) };
    });
  };

  // KuikaCollapsePanel renders provided.placeholder as a SIBLING *after* the collapse, i.e. outside
  // the folder content. That misplaces RBD's reserved slot: while dragging into a folder its items
  // slide to open a gap, but the collapse never gains height, so the last item overflows and overlaps
  // whatever sits below the folder. We strip the placeholder here and re-render it *inside* the
  // folder content (see the children fragments) so the collapse grows correctly and siblings below it
  // get pushed down. innerRef stays on the collapse root, so a collapsed folder's header is still a
  // hover target for onDragUpdate auto-expand.
  withoutPlaceholder = (provided: any) => {
    return { ...provided, placeholder: null };
  };

  deleteFolder = (folderId: string, parentFolderId?: string) => {
    if (parentFolderId) {
      deleteDesignerSubfolder(getSdInstance(), { folderType: "screen", parentFolderId, subFolderId: folderId });
    } else {
      deleteDesignerFolder(getSdInstance(), { folderType: "screen", folderId });
    }
  };

  editScreenName = (screenId: string) => {
    this.setState({ renamingScreenId: screenId, isScreenRenaming: true });
  };

  handleNameOnBlur = async (e) => {
    const newName: any = e.target?.value ?? e.state.value;
    this.changeScreenName(newName);
  };

  cancelRenameScreen = () => {
    this.setState({ isScreenRenaming: false, renamingScreenId: "" });
  };

  handleSearchFocus = (event) => event.target.select();

  handleNameKeypress = async (e) => {
    try {
      if (e.keyCode === 13) {
        this.handleNameOnBlur(e);
      } else if (e.keyCode === 27) {
        this.cancelRenameScreen();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  changeScreenName = async (newName: string) => {
    this.setState({ isRenameLoading: true });
    const outcome: ScreenRenameOutcome = await renameCurrentScreen(this.props.sd, newName);
    if (outcome === "rejected") {
      this.setState({ isRenameLoading: false });
      return;
    }
    this.cancelRenameScreen();
    this.setState({ isRenameLoading: false });
  };

  triggerDeleteModal = (screen: IScreenDesignInfo) => {
    if (!canOpenDeleteScreenModal(this.props.sd)) {
      return;
    }

    this.openModal("deleteScreen", { screenToDelete: screen });
  };

  getScreenType = (sd: IScreenDesignInfo): EScreenType => {
    return getScreenTypeOf(sd);
  };

  async deleteScreen() {
    if (!this.state.screenToDelete) {
      return;
    }
    await deleteScreenFromRow(this.props.sd, this.state.screenToDelete, this.props.designType, () =>
      this.getRecentlyOpenedScreens()
    );
  }

  duplicateScreen = (screenId: string, screenName: string) => {
    duplicateScreenFromRow(this.props.sd, screenId, screenName, this.props.showDomainAccessRequiredModal);
  };

  handleRowDragStart = (): void => {
    this.setState({ isRowDragging: true });
  };

  handleRowDragEnd = (result: DropResult): void => {
    this.setState({ isRowDragging: false });
    this.onDragEnd(result);
  };

  onDragEnd = (result: any) => {
    this.clearExpandHover();
    const { source, destination } = result;
    if (!source) {
      return;
    }

    // Reordering inside the same list, or dropping an already-unfoldered screen outside any
    // folder, changes no folder membership — skip the mutation and the (otherwise wasted) refresh.
    const isSameList = destination?.droppableId === source.droppableId;
    const isUnfolderedSource = source.droppableId === "droppable2";
    if (isSameList || (!destination && isUnfolderedSource)) {
      return;
    }

    const folder = this.props.sd.getFolderById(
      destination?.droppableId !== "droppable2" && destination?.droppableId
        ? destination?.droppableId
        : source.droppableId,
      this.props.sd.project.screenFolders
    );
    if (!destination || destination?.droppableId === "droppable2") {
      if (folder?.parentFolderId) {
        removeDesignerItemFromSubfolder(getSdInstance(), {
          folderType: "screen",
          parentFolderId: folder.parentFolderId,
          subFolderId: folder.id,
          itemId: result.draggableId
        });
      } else {
        removeDesignerItemFromFolder(getSdInstance(), {
          folderType: "screen",
          folderId: source.droppableId,
          itemId: result.draggableId
        });
      }
    } else if (folder?.parentFolderId) {
      addDesignerItemToSubfolder(getSdInstance(), {
        folderType: "screen",
        parentFolderId: folder.parentFolderId,
        subFolderId: destination?.droppableId,
        itemId: result.draggableId
      });
      // Drop landed in a sub-folder — open it and its parent so the moved screen is visible.
      this.ensureFoldersExpanded([folder.parentFolderId, destination?.droppableId]);
    } else {
      addDesignerItemToFolder(getSdInstance(), {
        folderType: "screen",
        folderId: destination?.droppableId,
        itemId: result.draggableId
      });
      // Drop landed in a (possibly collapsed) folder — auto-expand it to reveal the moved screen.
      this.ensureFoldersExpanded([destination?.droppableId]);
    }

    // The folder mutations above update project.screenFolders synchronously and in place, but the
    // only re-render they trigger is the setState that runs *after* the async project PATCH resolves
    // (processUpdateProject) — so until then the panel shows stale folder contents and the user has
    // to toggle a folder to force a render over the already-mutated data. rename/delete/duplicate
    // avoid this by firing the synchronous reload contract; the drag path was the one mutation that
    // skipped it. Restore parity so the panel refreshes immediately on drop.
    this.props.sd.setIsScreenWindowReload(true);
  };

  // Spring-loaded expand: open a collapsed folder only after the drag dwells over it briefly, so we
  // don't fire a setState (full panel re-render) for every folder the cursor merely passes over —
  // that churn was the source of drag lag. Only the folder you intend to drop into pays the cost.
  expandHoverTimer: ReturnType<typeof setTimeout> | null = null;
  expandHoverTargetId: string | null = null;

  clearExpandHover = () => {
    if (this.expandHoverTimer) {
      clearTimeout(this.expandHoverTimer);
      this.expandHoverTimer = null;
    }
    this.expandHoverTargetId = null;
  };

  onDragUpdate = (update: any) => {
    const destId: string | undefined = update?.destination?.droppableId;
    if (!destId || destId === "droppable2") {
      this.clearExpandHover();
      return;
    }
    if (destId === this.expandHoverTargetId) {
      return;
    }
    this.clearExpandHover();
    const folder: IScreenFolder | undefined = this.props.sd.getFolderById(destId, this.props.sd.project.screenFolders);
    if (!folder) {
      return;
    }
    const parentId: string | undefined = folder.parentFolderId;
    const isAlreadyOpen: boolean =
      this.state.activeKeys.includes(destId) && (!parentId || this.state.activeKeys.includes(parentId));
    if (isAlreadyOpen) {
      return;
    }
    this.expandHoverTargetId = destId;
    this.expandHoverTimer = setTimeout(() => {
      this.ensureFoldersExpanded([parentId, destId]);
      this.expandHoverTimer = null;
    }, 350);
  };

  deleteSearchText = (e: React.MouseEvent) => {
    e.stopPropagation();

    const viewPart = this.props.designType === "report" ? ViewPart.ReportDesignerPagesPanel : ViewPart.ScreensPanel;

    this.props.sd.setSearchTexts(viewPart, "");

    this.setState({ searchText: "", isSearchActive: false });
  };

  editFolderMenu = (folder: IScreenFolder) => (
    <div onClick={(e) => e.stopPropagation()}>
      <KuikaDropdownMenu
        options={this.folderActionMenu(folder)}
        overlayClassName="kuika-screens__dropdown"
        trigger="click"
      >
        {(open) => (
          <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
            <KuikaSvg
              id={`${folder.id}FolderDotsIcon`}
              data-testid={`${folder.name}FolderDotMenu`.replace(/\s/g, "")}
              data-onboarding-id={`left-panel_screens_${folder.name}-folder-menu`.replace(/\s/g, "")}
              width={16}
              height={16}
              name="dots"
            />
          </KuikaButton>
        )}
      </KuikaDropdownMenu>
    </div>
  );

  folderActionMenu = (folder: IScreenFolder) => {
    let parentFolder;
    if (folder.parentFolderId) {
      parentFolder = this.props.sd.project?.screenFolders?.find((x) => x.id === folder.parentFolderId);
    }
    const folderTarget: IPendingScreenFolderTarget = {
      folderId: folder.id,
      parentFolderId: folder.parentFolderId
    };
    const menu: any = [
      {
        label: "New Screen",
        icon: (
          <KuikaSvg
            id={`${folder.id}NewScreenInFolderIcon`}
            data-testid={`${folder.name}FolderNewScreen`.replace(/\s/g, "")}
            data-onboarding-id={`left-panel_screens_${folder.name}-folder-new-screen`.replace(/\s/g, "")}
            width={16}
            height={16}
            name="file-plus"
            type="sidebar-icons"
          />
        ),
        onClick: (e) => this.handleCreateModal(e, EScreenType.Default, folderTarget)
      },
      !parentFolder && {
        label: "Add sub-folder",
        icon: (
          <KuikaSvg
            id="addSubFolderIcon"
            data-testid="addSubFolderButton"
            data-onboarding-id="left-panel_screens_add-subfolder"
            width={16}
            height={16}
            name="folder-plus"
            type="sidebar-icons"
          />
        ),
        onClick: () => this.openModal("newFolder", { folderParentFolderId: folder.id })
      },
      {
        label: "Rename",
        icon: (
          <KuikaSvg
            id="renameFolderIcon"
            data-onboarding-id="left-panel_screens_rename-folder"
            width={16}
            height={16}
            name="edit"
          />
        ),
        onClick: () =>
          this.openModal("editFolder", {
            editingFolder: folder,
            folderParentFolderId: folder.parentFolderId ?? null
          })
      },
      {
        label: <KuikaText>Delete</KuikaText>,
        icon: (
          <KuikaSvg
            id="deleteFolderIcon"
            data-testid="deleteButton"
            data-onboarding-id="left-panel_screens_delete-folder"
            width={16}
            height={16}
            name="trash"
          />
        ),
        onClick: () => this.deleteFolder(folder.id, folder?.parentFolderId),
        danger: true
      }
    ].filter(Boolean);

    return menu;
  };

  getScreensDropdown = (type: EScreenType) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu
          options={this.screenListActionMenu(type)}
          overlayClassName="kuika-screens__dropdown"
          trigger="click"
        >
          {(open) => (
            <KuikaButton
              data-testid={`${type}newScreenPlusButton`}
              data-onboarding-id={`left-panel_screens_${type}-new-screen-plus`}
              isIcon
              type="tertiary"
              color="neutral"
              active={open}
            >
              <KuikaSvg id={`${type}newScreenPlusIcon`} width={16} height={16} name="plus" />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  getScreenDropdown = (screen: IScreenDesignInfo) => {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <KuikaDropdownMenu
          options={this.screenActionMenu(screen)}
          overlayClassName="kuika-screens__dropdown"
          trigger="click"
        >
          {(open) => (
            <KuikaButton isIcon type="tertiary" color="neutral" active={open}>
              <KuikaSvg
                id={`${screen.id}ThreeDotMenuIcon`}
                data-testid={`${screen.name.toLowerCase()}ThreeDotMenu`}
                data-onboarding-id={`left-panel_screens_${screen.name.toLowerCase()}-menu`}
                width={16}
                height={16}
                name="dots"
              />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </div>
    );
  };

  getMasterScreensDropdown = () => {
    const hasAccessCreateScreen = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateScreen,
      this.props.sd.project?.projectAccess
    );
    if (!hasAccessCreateScreen) {
      return <></>;
    }

    return (
      <KuikaButton
        data-testid="masterScreenPlusButton"
        data-onboarding-id="left-panel_screens_master-plus"
        onClick={(e) => this.handleCreateModal(e, EScreenType.Master)}
        isIcon
        type="tertiary"
        color="neutral"
      >
        <KuikaSvg id="masterScreenPlusIcon" width={16} height={16} name="plus" />
      </KuikaButton>
    );
  };

  getWorkflowScreensDropdown = () => {
    return (
      <KuikaButton
        data-testid="workflowEditButton"
        data-onboarding-id="left-panel_screens_workflow-edit"
        onClick={this.handleManageWorkflowScreens}
        isIcon
        type="tertiary"
      >
        <KuikaSvg id="workflowEditIcon" width={16} height={16} name="edit" />
      </KuikaButton>
    );
  };

  screenListActionMenu = (type: EScreenType) => {
    const hasAccessCreateScreen = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateScreen,
      this.props.sd.project?.projectAccess
    );
    const menu: any = [
      {
        label: "New Screen",
        icon: (
          <KuikaSvg
            id={`${type}newScreenMenuIcon`}
            data-testid={`${type}newScreenButton`}
            data-onboarding-id={`left-panel_screens_${type}-new-screen`}
            width={16}
            height={16}
            name="file-plus"
            type="sidebar-icons"
          />
        ),
        onClick: (e) => this.handleCreateModal(e, type)
      },
      type !== EScreenType.WebScreen &&
        hasAccessCreateScreen && {
          label: "New Folder",
          icon: (
            <KuikaSvg
              id={`${type}newFolderMenuIcon`}
              data-testid={`${type}newFolderButton`}
              data-onboarding-id={`left-panel_screens_${type}-new-folder`}
              width={16}
              height={16}
              name="folder-plus"
              type="sidebar-icons"
            />
          ),
          onClick: () => this.openModal("newFolder")
        }
    ].filter(Boolean);
    return menu;
  };

  screenActionMenu = (screen: IScreenDesignInfo): KuikaDropdownMenuItemType[] =>
    buildScreenActionMenu({
      sd: this.props.sd,
      screen,
      isCurrentScreen: this.isScreenCurScreen(screen.id),
      username: this.props?.context.userInfo?.username,
      handlers: {
        onRename: (screenId: string) => this.editScreenName(screenId),
        onDuplicate: (target: IScreenDesignInfo) => this.duplicateScreen(target.id, target.name),
        onExport: () => exportScreenKuikaFile(this.props.sd),
        onDelete: (target: IScreenDesignInfo) => this.triggerDeleteModal(target)
      }
    });

  /**
   * Workflow-screen management lives on the process editor's Forms tab now — the legacy
   * step-screen wizard modal is retired from the screen lists. Navigates to Process Automation
   * (selecting the process when the shortcut is process-scoped) and opens the Forms tab.
   */
  handleManageWorkflowScreens = (e: React.MouseEvent, processId?: string): void => {
    e.stopPropagation();
    void redirectToProcessForms(this.props.sd, processId);
  };

  handleCreateModal = (e: any, screenType: EScreenType, folderTarget?: IPendingScreenFolderTarget) => {
    if (screenType !== EScreenType.Default && screenType !== EScreenType.WebScreen) {
      e.stopPropagation();
    }
    if (screenType === EScreenType.WebScreen) {
      this.openNewScreenModal(
        this.props.sd.screenList.filter((screen) => this.getScreenType(screen) !== EScreenType.Report).length,
        screenType,
        folderTarget
      );
      return;
    }
    if (this.props.designType === "screen") {
      this.openNewScreenModal(
        this.props.sd.screenList.filter((screen) => this.getScreenType(screen) !== EScreenType.Report).length,
        screenType,
        folderTarget
      );
    } else if (this.props.designType === "report") {
      this.openModal("newReport");
    }
  };

  // "+" asks for the report right here — the same sheet the landing's card opens — rather than
  // sending the user to the landing list to ask for it again.
  onNewReportClick = (e?: React.MouseEvent): void => {
    e?.stopPropagation();
    this.openModal("newReport");
  };

  handleLoadScreen = (screen: any) => {
    this.props.sd.loadScreen(screen.id);
    if (
      this.props.sd.dashboardState === DashboardState.reportDesigner &&
      this.props.sd.isCreateReportDesignPageActive
    ) {
      this.props.sd.setVisibilityOfCreateReportDesignPage(false);
    }
    this.getRecentlyOpenedScreens();
  };

  handleSearch = (searchText: string) => {
    if (this.props.designType === "report") {
      this.props.sd.setSearchTexts(ViewPart.ReportDesignerPagesPanel, searchText);
    } else {
      this.props.sd.setSearchTexts(ViewPart.ScreensPanel, searchText);
    }
    this.setState({ searchText });
  };

  screenItem = (screen: any, provided: any, isManaged?: boolean, showDragHandle?: boolean) => {
    const hasAnyRowAction: boolean = hasAnyScreenRowAction(this.props.sd);

    const shouldShowDragHandle =
      showDragHandle && this.props.sd.project.screenFolders && this.props.sd.project.screenFolders.length > 0;

    return (
      <KuikaMenuItem
        key={screen.id}
        id={`screenMenuItem-${screen.id}`}
        title={
          this.state.isScreenRenaming === true && this.state.renamingScreenId === screen.id ? (
            // Stop clicks/mousedowns on the rename input from bubbling to the menu item's
            // onClick (which closes rename + loads the screen) and to the row's drag handle.
            <span onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
              <KuikaInput
                data-testid="renameInput"
                defaultValue={screen.name}
                onKeyDown={(e) => {
                  this.handleNameKeypress(e);
                }}
                onBlur={(e) => {
                  this.handleNameOnBlur(e);
                }}
                disabled={this.state.isRenameLoading}
                autoFocus
                className="kuika-menu-item__rename-input"
              />
            </span>
          ) : (
            screen.name
          )
        }
        provided={provided}
        selected={this.isScreenCurScreen(screen.id)}
        onClick={() => {
          this.setState({
            isScreenRenaming: false
          });
          this.props.sd.saveScreen(true).catch(() => {});
          this.handleLoadScreen(screen);
        }}
        leftIcon={this.getScreenRoleIcon(screen.id, isManaged)}
        showDragHandle={shouldShowDragHandle}
        rightDropdown={hasAnyRowAction && !this.state.isScreenRenaming && this.getScreenDropdown(screen)}
      />
    );
  };

  renderScreenListByType = (type: EScreenType, role?: "workflow") => {
    const { sd } = this.props;
    const { searchText } = this.state;
    let screens: IScreenDesignInfo[] = sd.screenList;

    const hasAccessCreateScreen = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateScreen,
      this.props.sd.project?.projectAccess
    );

    const filterScreensByType = (type: EScreenType) => {
      if (!Object.values(EScreenType).includes(type)) throw new Error("Invalid screen type");
      return screens.filter((screen) => this.getScreenType(screen) === type);
    };

    const filterScreensByRole = (role: any, project: any) => {
      return screens.filter((screen) => {
        const screenWorkflow = project?.processStepScreenIds?.find((x) => x.screenId === screen.id);
        return role === "workflow" ? !!screenWorkflow : !screenWorkflow;
      });
    };

    const filterScreensBySearchText = (searchText: string) => {
      const normalizedSearchText = KMainFunctions.turkishCharsToEnglish(searchText).toLowerCase();
      return screens.filter((screen) => {
        const screenName = screen?.name?.toLowerCase() ?? "";
        return screenName.includes(normalizedSearchText) || screenName.startsWith(normalizedSearchText);
      });
    };

    const filterScreensNotInFolders = () => {
      const projectFolders = sd.project.screenFolders ?? [];
      return screens.filter((screen) => {
        const isInFolder = projectFolders.some((folder) => folder.screenIds?.includes(screen.id));
        const folder = sd.findFolderByScreenId(screen.id, projectFolders);
        return !isInFolder && !folder;
      });
    };

    screens = type ? filterScreensByType(type) : screens;

    const project = sd.project;
    if (project) {
      screens = filterScreensByRole(role, project);
    }

    if (searchText?.length > 0) {
      screens = filterScreensBySearchText(searchText);
    }

    screens = filterScreensNotInFolders();

    screens = screens.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));

    const managedWfScreenIds: string[] =
      this.props.sd.project?.processStepScreenIds?.filter((x) => x.isManaged)?.map((x) => x.screenId) ?? [];

    // Group screens by workflow when role is "workflow" and there are multiple workflows
    if (role === "workflow" && this.props.sd.workFlowList.length > 1) {
      const workflowGroups = new Map<string, IScreenDesignInfo[]>();

      screens.forEach((screen) => {
        const processStepInfo = project?.processStepScreenIds?.find((x) => x.screenId === screen.id);
        if (processStepInfo) {
          const processId = processStepInfo.processId;
          if (!workflowGroups.has(processId)) {
            workflowGroups.set(processId, []);
          }
          workflowGroups.get(processId)?.push(screen);
        }
      });

      return (
        <DragDropContext onDragStart={this.handleRowDragStart} onDragEnd={this.handleRowDragEnd}>
          {Array.from(workflowGroups.entries())
            .sort((a, b) => {
              const workflowA = this.props.sd.workFlowList.find((wf) => wf.id === a[0]);
              const workflowB = this.props.sd.workFlowList.find((wf) => wf.id === b[0]);
              const nameA = workflowA?.displayName || workflowA?.name || "";
              const nameB = workflowB?.displayName || workflowB?.name || "";
              return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
            })
            .map(([processId, workflowScreens]) => {
              const workflow = this.props.sd.workFlowList.find((wf) => wf.id === processId);
              if (!workflow) return null;

              return (
                <Droppable droppableId={`workflow-${processId}`} key={processId}>
                  {(provided, snapshot) => (
                    <>
                      <KuikaCollapsePanel
                        provided={provided}
                        activeKey={!(this.state.searchText.length > 0) ? this.state.activeKeys : [processId]}
                        onChange={this.onFolderPanelChange}
                        forceRender
                        expandIcon={({ isActive }) =>
                          isActive ? (
                            <KuikaSvg
                              id={`workflowExpandIcon-${processId}-open`}
                              name="k-down"
                              type="arrows"
                              width={16}
                              height={16}
                            />
                          ) : (
                            <KuikaSvg
                              id={`workflowExpandIcon-${processId}-closed`}
                              name="k-right"
                              type="arrows"
                              width={16}
                              height={16}
                            />
                          )
                        }
                        className={`kuika-left-panel__folder-collapse kuika-collapse-panel${
                          snapshot.isDraggingOver ? " kuika-left-panel__folder-collapse--drag-over" : ""
                        }`}
                        items={[
                          {
                            label: (
                              <div className="kuika-flex-center--gap">
                                <KuikaSvg
                                  id={`workflowIcon-${processId}`}
                                  name="workflow-managed"
                                  type="workflow-icons"
                                  width={16}
                                  height={16}
                                />
                                <KuikaText>{workflow.displayName || workflow.name}</KuikaText>
                              </div>
                            ),
                            key: processId,
                            children: (
                              <>
                                {workflowScreens
                                  ?.sort((a, b) =>
                                    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
                                  )
                                  ?.map((screen, index) => (
                                    <Draggable
                                      key={screen.id}
                                      draggableId={screen.id}
                                      index={index}
                                      isDragDisabled={true}
                                    >
                                      {(provided, snapshot) =>
                                        this.screenItem(screen, provided, managedWfScreenIds.includes(screen.id), false)
                                      }
                                    </Draggable>
                                  ))}
                                {workflowScreens.length === 0 && hasAccessCreateScreen && (
                                  <KuikaEmptyState
                                    svgName={"automation"}
                                    name={"Workflow Screen"}
                                    description={"to manage your processes effortlessly."}
                                    onDescriptionClick={(e: React.MouseEvent<HTMLButtonElement>): void =>
                                      this.handleManageWorkflowScreens(e, processId)
                                    }
                                  />
                                )}
                              </>
                            )
                          }
                        ]}
                      />
                      {provided.placeholder}
                    </>
                  )}
                </Droppable>
              );
            })}
        </DragDropContext>
      );
    }

    return (
      <DragDropContext
        onDragStart={this.handleRowDragStart}
        onDragEnd={this.handleRowDragEnd}
        onDragUpdate={this.onDragUpdate}
      >
        {type === EScreenType.Default &&
          role !== "workflow" &&
          this.props.sd.project.screenFolders
            ?.filter((folder) => {
              const folderMatches = folder.name
                .toLowerCase()
                .includes(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase());

              const folderScreenMatches = folder.screenIds?.some((screenId) => {
                const screenX = this.props.sd.screenList?.find((screen) => screen.id === screenId);
                return screenX?.name
                  ?.toLowerCase()
                  .includes(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase());
              });

              const childMatches = folder.children?.some((child) => {
                const childNameMatches = child.name
                  .toLowerCase()
                  .includes(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase());

                const childScreenMatches = child.screenIds?.some((screenId) => {
                  const screenX = this.props.sd.screenList?.find((screen) => screen.id === screenId);
                  return screenX?.name
                    ?.toLowerCase()
                    .includes(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase());
                });

                return childNameMatches || childScreenMatches;
              });

              return folderMatches || folderScreenMatches || childMatches;
            })
            ?.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }))
            ?.map((folder) => (
              <Droppable droppableId={folder.id} key={folder.id}>
                {(provided, snapshot) => (
                  <>
                    <KuikaCollapsePanel
                      // Marks the folder's whole collapse (header + content) so a test can scope a
                      // screen row to THIS folder; the row itself is identical at root and in a folder.
                      data-testid={`${folder.name}FolderCollapse`.replace(/\s/g, "")}
                      provided={this.withoutPlaceholder(provided)}
                      activeKey={!(this.state.searchText.length > 0) ? this.state.activeKeys : [folder.id]}
                      onChange={this.onFolderPanelChange}
                      forceRender
                      expandIcon={({ isActive }) =>
                        isActive ? (
                          <KuikaSvg
                            id={`folderExpandIcon-${folder.id}-open`}
                            name="k-down"
                            type="arrows"
                            width={16}
                            height={16}
                          />
                        ) : (
                          <KuikaSvg
                            id={`folderExpandIcon-${folder.id}-closed`}
                            name="k-right"
                            type="arrows"
                            width={16}
                            height={16}
                          />
                        )
                      }
                      className={`kuika-left-panel__folder-collapse kuika-collapse-panel${
                        snapshot.isDraggingOver ? " kuika-left-panel__folder-collapse--drag-over" : ""
                      }`}
                      items={[
                        {
                          label: (
                            // The folder's name had no test-id, so the E2E suite addressed it
                            // through KuikaText's `title` attribute — which, because this label's
                            // children are [icon, name], serialises as " ,<name>" for a nested
                            // folder and as "<name>" for a top-level one. Two different shapes for
                            // the same thing, both depending on how React coerces a children array
                            // (QA-484, scenario 14). Named like its siblings on this row
                            // (`<name>FolderDotMenu`, `<name>FolderNewScreen`) and on the plain
                            // div rather than KuikaText, so nothing has to forward the prop.
                            <div
                              className="kuika-flex-center--gap"
                              data-testid={`${folder.name}FolderLabel`.replace(/\s/g, "")}
                            >
                              <KuikaSvg
                                id={`folderIcon-${folder.id}`}
                                name="folder"
                                type="sidebar-icons"
                                width={16}
                                height={16}
                              />
                              <KuikaText>{folder.name}</KuikaText>
                            </div>
                          ),
                          key: folder.id,
                          extra: this.editFolderMenu(folder),
                          children: (
                            <>
                              {folder.screenIds && folder.screenIds.length > 0 ? (
                                this.props.sd.screenList
                                  ?.filter(
                                    (x) =>
                                      x.name
                                        .toLowerCase()
                                        .indexOf(
                                          KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()
                                        ) !== -1 ||
                                      x?.name
                                        ?.toLowerCase()
                                        .startsWith(
                                          KMainFunctions.turkishCharsToEnglish(this.state.searchText)?.toLowerCase()
                                        ) ||
                                      this.props.sd
                                        .findFolderByScreenId(x.id, this.props.sd.project.screenFolders)
                                        ?.name?.toLowerCase()
                                        .includes(
                                          KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()
                                        )
                                  )
                                  ?.filter((screen) => folder.screenIds?.includes(screen.id))
                                  ?.sort((a, b) =>
                                    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
                                  )
                                  ?.map((screen, index) => (
                                    <Draggable
                                      key={screen.id}
                                      draggableId={screen.id}
                                      index={index}
                                      isDragDisabled={
                                        !this.props.sd.project.screenFolders ||
                                        this.props.sd.project.screenFolders.length === 0
                                      }
                                    >
                                      {(provided, snapshot) => this.screenItem(screen, provided, undefined, true)}
                                    </Draggable>
                                  ))
                              ) : folder.children && folder.children.length > 0 ? (
                                <></>
                              ) : (
                                <KuikaEmptyState
                                  svgName="file"
                                  name={"Screen"}
                                  description={"to create screens."}
                                  onDescriptionClick={(e) =>
                                    this.handleCreateModal(e, EScreenType.Default, {
                                      folderId: folder.id,
                                      parentFolderId: folder.parentFolderId
                                    })
                                  }
                                />
                              )}
                              {folder.children &&
                                folder.children.length > 0 &&
                                folder.children.map((childFolder) => (
                                  <Droppable key={childFolder.id} droppableId={childFolder.id}>
                                    {(_provided, snapshot) => (
                                      <KuikaCollapsePanel
                                        provided={this.withoutPlaceholder(_provided)}
                                        activeKey={
                                          !(this.state.searchText.length > 0) ? this.state.activeKeys : [childFolder.id]
                                        }
                                        onChange={this.onFolderPanelChange}
                                        className={`kuika-left-panel__folder-collapse kuika-collapse-panel${
                                          snapshot.isDraggingOver ? " kuika-left-panel__folder-collapse--drag-over" : ""
                                        }`}
                                        expandIcon={({ isActive }) =>
                                          isActive ? (
                                            <KuikaSvg
                                              id={`childFolderExpandIcon-${childFolder.id}-open`}
                                              name="k-down"
                                              type="arrows"
                                              width={16}
                                              height={16}
                                            />
                                          ) : (
                                            <KuikaSvg
                                              id={`childFolderExpandIcon-${childFolder.id}-closed`}
                                              name="k-right"
                                              type="arrows"
                                              width={16}
                                              height={16}
                                            />
                                          )
                                        }
                                        forceRender
                                        items={[
                                          {
                                            label: (
                                              // Same `<name>FolderLabel` convention as the top-level folder above
                                              // (QA-484), so a sub-folder is addressable the same way a test
                                              // already addresses a root folder.
                                              <div
                                                className="kuika-flex-center--gap"
                                                data-testid={`${childFolder.name}FolderLabel`.replace(/\s/g, "")}
                                              >
                                                <KuikaSvg
                                                  id={`childFolderIcon-${childFolder.id}`}
                                                  name="folder"
                                                  type="sidebar-icons"
                                                  width={16}
                                                  height={16}
                                                />
                                                <KuikaText> {childFolder.name}</KuikaText>
                                              </div>
                                            ),
                                            key: childFolder.id,
                                            extra: this.editFolderMenu(childFolder),
                                            children: (
                                              <>
                                                {childFolder.screenIds && childFolder.screenIds.length > 0 ? (
                                                  this.props.sd.screenList
                                                    ?.filter(
                                                      (x) =>
                                                        x.name
                                                          .toLowerCase()
                                                          .indexOf(
                                                            KMainFunctions.turkishCharsToEnglish(
                                                              this.state.searchText
                                                            ).toLowerCase()
                                                          ) !== -1 ||
                                                        x?.name
                                                          ?.toLowerCase()
                                                          .startsWith(
                                                            KMainFunctions.turkishCharsToEnglish(
                                                              this.state.searchText
                                                            )?.toLowerCase()
                                                          )
                                                    )
                                                    ?.filter((screen) => childFolder.screenIds?.includes(screen.id))
                                                    ?.sort((a, b) =>
                                                      a.name.localeCompare(b.name, undefined, {
                                                        numeric: true,
                                                        sensitivity: "base"
                                                      })
                                                    )
                                                    .map((screen, index) => (
                                                      <Draggable
                                                        key={screen.id}
                                                        draggableId={screen.id}
                                                        index={index}
                                                        isDragDisabled={
                                                          !this.props.sd.project.screenFolders ||
                                                          this.props.sd.project.screenFolders.length === 0
                                                        }
                                                      >
                                                        {(_provided2, snapshot) =>
                                                          this.screenItem(screen, _provided2, undefined, true)
                                                        }
                                                      </Draggable>
                                                    ))
                                                ) : (
                                                  <KuikaEmptyState
                                                    svgName="file"
                                                    name={"Screen"}
                                                    description={"to create screens."}
                                                    onDescriptionClick={(e) =>
                                                      this.handleCreateModal(e, EScreenType.Default, {
                                                        folderId: childFolder.id,
                                                        parentFolderId: folder.id
                                                      })
                                                    }
                                                  />
                                                )}
                                                {/* Reserve the drop slot inside the sub-folder content
                                                    so it grows and items below get pushed down. */}
                                                {_provided.placeholder}
                                              </>
                                            )
                                          }
                                        ]}
                                      />
                                    )}
                                  </Droppable>
                                ))}
                              {/* Reserve the drop slot INSIDE the folder content so the collapse grows
                                  and items below get pushed down (avoids overlap). */}
                              {provided.placeholder}
                            </>
                          )
                        }
                      ]}
                    />
                  </>
                )}
              </Droppable>
            ))}
        <Droppable droppableId="droppable2">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              // The unfoldered (root-level) rows of this section. Type/role-scoped because this
              // list is rendered once per section (default, workflow, master, report).
              data-testid={role === "workflow" ? `${type}workflowRootScreensList` : `${type}rootScreensList`}
            >
              {screens?.map((screen, index) => (
                <Draggable
                  key={screen.id}
                  draggableId={screen.id}
                  index={index}
                  isDragDisabled={
                    !this.props.sd.project.screenFolders || this.props.sd.project.screenFolders?.length === 0
                  }
                >
                  {(provided, snapshot) =>
                    this.screenItem(
                      screen,
                      provided,
                      role === "workflow" ? managedWfScreenIds.includes(screen.id) : undefined,
                      type === EScreenType.Default
                    )
                  }
                </Draggable>
              ))}
              {screens.length === 0 && hasAccessCreateScreen && (
                <KuikaEmptyState
                  svgName={
                    role === "workflow"
                      ? "automation"
                      : type === EScreenType.Master
                        ? "device-desktop-star"
                        : type === EScreenType.Report
                          ? "file-text"
                          : "file"
                  }
                  name={
                    role === "workflow"
                      ? "Workflow Screen"
                      : type === EScreenType.Master
                        ? "Master Screen"
                        : type === EScreenType.Report
                          ? "Report"
                          : "Screen"
                  }
                  description={
                    role === "workflow"
                      ? "to manage your processes effortlessly."
                      : type === EScreenType.Master
                        ? "to create master screens."
                        : type === EScreenType.Report
                          ? "to create reports."
                          : "to create screens."
                  }
                  onDescriptionClick={
                    role === "workflow" ? this.handleManageWorkflowScreens : (e) => this.handleCreateModal(e, type)
                  }
                />
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  renderRecentlyOpenedScreens = () => {
    const hasRows: boolean = (this.state.recentlyOpenedScreens?.length ?? 0) > 0;

    return (
      <RecentlyVisitedRegion
        isLoading={this.state.isRecentlyOpenedLoading}
        hasRows={hasRows}
        expectedRows={this.state.expectedRecentRows}
      >
        {hasRows ? this.renderRecentlyOpenedRows() : <></>}
      </RecentlyVisitedRegion>
    );
  };

  renderRecentlyOpenedRows = () => {
    return (
      <DragDropContext onDragStart={this.handleRowDragStart} onDragEnd={this.handleRowDragEnd}>
        <Droppable droppableId="droppable2">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {this.state.recentlyOpenedScreens
                ?.filter((x) => {
                  const screenInfo = this.props.sd.screenList?.find((s) => s.id === x.screenId);
                  return screenInfo ? this.getScreenType(screenInfo) !== EScreenType.Report : true;
                })
                ?.filter(
                  (x) =>
                    x.screenName
                      .toLowerCase()
                      .indexOf(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()) !== -1 ||
                    x?.screenName
                      ?.toLowerCase()
                      .startsWith(KMainFunctions.turkishCharsToEnglish(this.state.searchText)?.toLowerCase())
                )
                ?.filter((x) => x.screenId !== this.props.sd.sdId)
                ?.map((screen, index) => (
                  <Draggable key={screen.screenId} draggableId={screen.screenId} index={index} isDragDisabled={true}>
                    {(provided, snapshot) =>
                      this.screenItem(
                        {
                          id: screen.screenId,
                          name: screen.screenName
                        },
                        undefined
                      )
                    }
                  </Draggable>
                ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  renderWebScreens = () => {
    const screens = this.props.sd.screenList.filter((x) => this.getScreenType(x) === EScreenType.WebScreen);
    return (
      <>
        {screens
          ?.filter(
            (x) =>
              x.name
                .toLowerCase()
                .indexOf(KMainFunctions.turkishCharsToEnglish(this.state.searchText).toLowerCase()) !== -1 ||
              x?.name
                ?.toLowerCase()
                .startsWith(KMainFunctions.turkishCharsToEnglish(this.state.searchText)?.toLowerCase())
          )
          ?.filter((x) => !this.props.sd.project.screenFolders?.find((y) => y.screenIds?.includes(x.id)))
          ?.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }))
          ?.map((screen, index) => this.screenItem(screen, undefined))}

        {!screens ||
          (screens?.length === 0 && (
            <span className="kuika-folder__empty-text" style={{ marginLeft: 24 }}>
              <KuikaEmptyState
                svgName={"device-imac"}
                name={"Web Screen"}
                description={"to create web screens."}
                onDescriptionClick={(e) => this.handleCreateModal(e, EScreenType.WebScreen)}
              />
            </span>
          ))}
      </>
    );
  };

  render() {
    const hasAccessCreateScreen = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateScreen,
      this.props.sd.project?.projectAccess
    );
    const collapseItems: any = [
      this.props.sd.getScreenType() !== EScreenType.Report &&
        this.props.designType === "screen" &&
        this.state.recentlyOpenedScreens && {
          label: <KuikaText id="recentlyVisitedHeader">Recently Visited</KuikaText>,
          key: "recentlyOpened",
          children: this.renderRecentlyOpenedScreens()
        },
      this.props.sd.getScreenType() !== EScreenType.Report &&
        this.props.designType === "screen" && {
          label: <KuikaTextMedium id="screensHeader">Screens</KuikaTextMedium>,
          key: "screens",
          children: this.renderScreenListByType(EScreenType.Default),
          extra: hasAccessCreateScreen ? this.getScreensDropdown(EScreenType.Default) : <></>
        },
      this.props.sd.project.projectType !== EProjectType.Desktop &&
        this.props.designType === "screen" && {
          label: <KuikaTextMedium id="webScreensHeader">Web Screens</KuikaTextMedium>,
          key: "webScreens",
          children: this.renderWebScreens(),
          extra: hasAccessCreateScreen ? this.getScreensDropdown(EScreenType.WebScreen) : <></>
        },
      KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
        EUserAccessPart.ManageWorkflow,
        this.props.sd.project?.projectAccess
      ) &&
        // The context starts life with `project: {} as IProject`, so the type's promise that this
        // array exists is only true once the project has loaded. Opening Screens before that threw
        // here and, with no error boundary above it, took the whole designer down to a blank page.
        // Every other reader of this field already guards it.
        this.props.sd.project?.processStepScreenIds?.length > 0 &&
        this.props.sd.getScreenType() !== EScreenType.Report &&
        this.props.designType === "screen" && {
          label: <KuikaTextMedium id="workflowScreensHeader">Workflow Screens</KuikaTextMedium>,
          key: "workflow-screens-panel",
          children: this.renderScreenListByType(EScreenType.Default, "workflow"),
          extra: this.getWorkflowScreensDropdown()
        },
      this.props.sd.getScreenType() !== EScreenType.Report &&
        this.props.designType === "screen" &&
        this.props.context.isMasterScreenActive && {
          label: <KuikaTextMedium id="masterScreensHeader">Master Screens</KuikaTextMedium>,
          key: "master",
          children: this.renderScreenListByType(EScreenType.Master),
          extra: this.getMasterScreensDropdown()
        }
    ].filter((item) => Boolean(item));

    return (
      <>
        <ScreenAddComponent
          showModal={this.state.modals.newScreen || this.props.sd.isNewScreenModalVisible}
          closeModal={this.closeNewScreenModal}
          projectId={this.props?.sd?.project?.id}
          componentSetId={this.props?.sd?.project?.componentSetObj?.id}
          creatingScreenType={this.getCreatingScreenType()}
          isOpenedFromLeftPanel={!this.props.sd.isNewScreenModalVisible}
        ></ScreenAddComponent>
        <ScreenFolderAddComponent
          showEditModal={this.state.modals.editFolder}
          editingFolderId={this.state.editingFolder?.id}
          editingFolderName={this.state.editingFolder?.name}
          closeEditModal={() => {
            this.closeModal("editFolder", {
              editingFolder: null,
              folderParentFolderId: undefined
            });
          }}
          projectId={this.props?.sd?.project?.id}
          componentSetId={this.props?.sd?.project?.componentSetObj?.id}
          folderParentFolderId={this.state.folderParentFolderId}
        ></ScreenFolderAddComponent>
        <ScreenFolderAddComponent
          showModal={this.state.modals.newFolder || this.props.sd.isNewFolderModalVisible}
          closeModal={() => {
            this.closeModal("newFolder", { folderParentFolderId: undefined });
            this.props.sd.setNewFolderModalVisibility(false);
          }}
          projectId={this.props?.sd?.project?.id}
          componentSetId={this.props?.sd?.project?.componentSetObj?.id}
          folderParentFolderId={this.state.folderParentFolderId}
        ></ScreenFolderAddComponent>
        {/* The sheet creates the report and opens it itself, so the panel only owns whether it shows. */}
        {this.props.designType === "report" && (
          <ReportCreateModal visible={this.state.modals.newReport} onClose={() => this.closeModal("newReport")} />
        )}
        <DeleteModal
          itemName={this.state.screenToDelete?.name ?? ""}
          triggerModal={this.state.modals.deleteScreen}
          deleteItem={() => this.deleteScreen()}
          deleteType={this.props.designType}
          closeModal={() => this.closeModal("deleteScreen")}
        />

        <div className="kuika-left-panel__container">
          <SearchablePanelHeader
            searchText={this.state.searchText}
            onSearchTextChange={this.handleSearch}
            onSearchClearCallback={this.deleteSearchText}
            viewPart={this.props.designType === "report" ? ViewPart.ReportDesignerPagesPanel : ViewPart.ScreensPanel}
            panelName={this.props.designType === "screen" ? "Screens" : "Reports"}
            isSearchActive={this.state.isSearchActive}
            setSearchActive={(value: boolean) => this.setState({ isSearchActive: value })}
            extraRightUnit={
              this.props.designType === "report" ? (
                <KuikaButton
                  data-testid="addReportPlusButton"
                  onClick={this.onNewReportClick}
                  isIcon
                  type="tertiary"
                  color="neutral"
                  size="sm"
                >
                  <KuikaSvg id="addReportPlusIcon" width={16} height={16} name="plus" />
                </KuikaButton>
              ) : undefined
            }
          />
          <div className="kuika-left-panel__content kuika-screens__list" data-testid="screensPanelContent">
            {/* The rows' own wrapper, so the selected screen's fill follows folders opening above it. */}
            <div className="kuika-selection-indicator-host--menu-items">
              <KuikaSelectionIndicator hidden={this.state.isRowDragging} />
              {this.props.designType === "report" ? (
                this.renderScreenListByType(EScreenType.Report)
              ) : (
                <KuikaCollapsePanel
                  activeKey={this.state.activeSectionKeys}
                  onChange={this.onSectionPanelChange}
                  expandIcon={({ isActive }) =>
                    isActive ? (
                      <KuikaSvg
                        id="leftPanelCollapseExpandIcon-open"
                        name="k-down"
                        type="arrows"
                        width={16}
                        height={16}
                      />
                    ) : (
                      <KuikaSvg
                        id="leftPanelCollapseExpandIcon-closed"
                        name="k-right"
                        type="arrows"
                        width={16}
                        height={16}
                      />
                    )
                  }
                  items={collapseItems}
                />
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.Loading || changeReason === ESdChangeReason.ChangeDiFriendlyName) {
      return true;
    }
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const screensWindow = connect(
  null,
  mapDispatchToProps
)(withContext<IScreensWindowProps>(withHistory<IScreensWindowProps>(withSD<IScreensWindowProps>(ScreensWindow))));
export { screensWindow as ScreensWindow };
